import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnDialogService } from '@lib/ui/dialog';

import { LprCameraType, LprIntegrationType } from '../constants/lpr-camera.constants';
import { IFsLprVigilantParameters } from '../interfaces';
import {
  FsLprCamera,
  SnLprRead,
  SnLprVigilantRead,
  SnLprFlockRead,
  VigilantResponse,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class LprReadsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getReadsList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnLprRead>> {
    if (!params?.ordering) {
      params.ordering = '-event_timestamp';
    }
    if (params.dist) {
      params.distance = params.dist / 1609.344;
    }
    if (Array.isArray(params.details__camera_name__in)) {
      params.details__camera_name__in = params.details__camera_name__in.join(',');
    }
    delete params.type__in;
    return this._httpClient.get('/api/lpr/reads/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((read) => new SnLprRead(read)),
      })),
    );
  }

  public getVigilantPlates(params: IFsLprVigilantParameters): Observable<{
    count: number;
    results: SnLprVigilantRead[];
  }> {
    Object.keys(params).forEach((param) => {
      if (params[param] === null || params[param] === undefined) {
        delete params[param];
      }
    });

    return (
      this._httpClient
        // note: object params can be passed into httpClient instead of HttpParams
        .get('/api/integrations/vigilant_ps/plate_search/', { params: params as any })
        .pipe(
          map((response: VigilantResponse) => {
            return {
              count: response.results.length || 0,
              results: response.results.map((read) => new SnLprVigilantRead(read)),
            };
          }),
          catchError((error) => {
            return throwError(error);
          }),
        )
    );
  }

  public getFlockPlates(params: ISnListQueryParameters): Observable<any> {
    return this._httpClient
      .get('/api/integrations/flock/plate_search/', {
        params,
        headers: {
          'X-fs-skip-error': 'true',
        },
      })
      .pipe(
        map((response: any) => ({
          count: response.count || response.results?.length,
          totalPages: response.totalPages,
          results: response.results.map((read) => new SnLprFlockRead(read)),
          links: response.links,
        })),
      );
  }

  public getVigilantPlateImage(key: string): Observable<Blob> {
    return this._httpClient.get('/api/integrations/vigilant_ps/get_image/', {
      responseType: 'blob',
      params: {
        image_key: key,
      },
    });
  }

  public createLprReport(data: any): Observable<any> {
    return this._httpClient.post('/api/lpr/reports/v2/reads/', data).pipe(
      tap({
        next: () => this._toastrService.success('Report Created'),
        error: () => this._toastrService.error('Report cannot be created. Please try again later'),
      }),
    );
  }

  public deleteLprReport(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'Are you sure you want to delete this report?',
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/lpr/reports/v2/reads/${id}/`)),
        tap({
          next: () => this._toastrService.success('Report deleted'),
          error: () =>
            this._toastrService.error(
              'Report cannot be deleted. You may not have proper permission',
            ),
        }),
      );
  }

  public getLprCameraList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsLprCamera>> {
    return this._httpClient.get('/api/lpr/cameras/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((read) => new FsLprCamera(read)),
      })),
    );
  }

  public getFlockCameraList(params?: ISnListQueryParameters): Observable<FsLprCamera[]> {
    return this._httpClient.get('/api/integrations/flock/cameras/', { params }).pipe(
      map((results: any[]) => {
        return results.map((r) => {
          return new FsLprCamera({
            ...r,
            integrationType: LprIntegrationType.FLOCK,
            type: LprCameraType.FS_ALPR,
            up: true,
            integrationId: r.id,
            position: r.geometry,
          });
        });
      }),
    );
  }

  public getFlockV3CameraList(params?: ISnListQueryParameters): Observable<FsLprCamera[]> {
    return this._httpClient.get('/api/integrations/flock_v3/cameras/', { params }).pipe(
      map((response: unknown) => {
        // Handle different response structures
        let cameras: unknown[] = [];
        if (Array.isArray(response)) {
          cameras = response;
        } else if (response && typeof response === 'object' && 'results' in response) {
          cameras = (response as { results: unknown[] }).results || [];
        }

        return cameras.map((camera: unknown) => {
          const cameraData = camera as Record<string, unknown>;
          return new FsLprCamera({
            ...cameraData,
            integrationType: LprIntegrationType.FLOCK_V3,
            type: LprCameraType.FS_ALPR,
            up: true,
            integrationId: String(cameraData.id || ''),
            position: {
              coordinates: [Number(cameraData.longitude || 0), Number(cameraData.latitude || 0)],
            },
            name: String(cameraData.name || cameraData.id || ''),
            id: String(cameraData.id || ''),
            lastObservedDate: new Date(),
            readsOnLastObserved: 0,
            properties: {
              id: String(cameraData.id || ''),
              name: String(cameraData.name || cameraData.id || ''),
            },
          });
        });
      }),
    );
  }
}
